import React, { useEffect } from "react";
import Select, { components } from "react-select";
import { Controller, useWatch } from "react-hook-form";
import styles from "./MentorshipSelect.module.css";

/* ================= STYLES ================= */

const customStyles = {
  control: (base) => ({
    ...base,
    border: "none",
    boxShadow: "none",
    minHeight: "40px",
    borderRadius: "9999px",
    backgroundColor: "transparent",
    "&:hover": { border: "none" },
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base) => ({
    ...base,
    color: "#bfc5ce",
    paddingRight: 12,
  }),
};

/* ================= OPTION ================= */

const Option = (props) => (
  <components.Option {...props}>
    <input
      type="checkbox"
      checked={props.isSelected}
      onChange={() => null}
      style={{ marginRight: 8 }}
    />
    {props.label}
  </components.Option>
);

/* ================= COMPONENT ================= */

const MentorshipSelect = ({
  control,
  name,
  label,
  options,
  errors,
  setValue,
  fullWidth,
  disabled,
}) => {
  /* values populated by reset() */
  const selectedValues = useWatch({ control, name, defaultValue: [] });
  const otherValue = useWatch({
    control,
    name: `${name}Other`,
    defaultValue: "",
  });

  /* ✅ Robust "Other" detection */
  const isOtherSelected = Array.isArray(selectedValues)
    ? selectedValues.some((val) => {
        const opt = options.find((o) => o.value === val);
        return opt?.label?.toLowerCase().includes("other");
      })
    : false;

  /* show when API has other OR user selects it */
  const showOtherInput = isOtherSelected || Boolean(otherValue);

  /* clear only when user removes "Other" */
  useEffect(() => {
    if (!isOtherSelected && otherValue) {
      setValue(`${name}Other`, "", {
        shouldDirty: true,
        shouldTouch: false,
      });
    }
  }, [isOtherSelected, otherValue, name, setValue]);

  return (
    <div className={!fullWidth && styles.grid}>
      <fieldset
        className={`${styles.fieldset}  ${disabled ? styles.disabled : ""} ${
          errors?.[name] ? styles.error : ""
        }`}
      >
        <legend className={styles.legend}>{label}</legend>

        <Controller
          name={name}
          control={control}
          render={({ field }) => {
            const selectValue = options.filter((opt) =>
              field.value?.includes(opt.value)
            );

            return (
              <Select
                options={options}
                value={selectValue}
                isMulti
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                components={{ Option }}
                placeholder="Select"
                styles={customStyles}
                isClearable={false}
                onChange={(selected) =>
                  field.onChange(selected ? selected.map((i) => i.value) : [])
                }
                isDisabled={disabled}
              />
            );
          }}
        />
      </fieldset>

      {/* ===== OTHER INPUT ===== */}
      {showOtherInput && (
        <Controller
          name={`${name}Other`}
          control={control}
          rules={{
            required: isOtherSelected ? "Please specify other role" : false,
          }}
          render={({ field }) => (
            <fieldset
              className={`${styles.fieldset}  ${
                disabled ? styles.disabled : ""
              } ${errors?.[`${name}Other`] ? styles.error : ""}`}
            >
              <legend className={styles.legend}>Other (Please Specify)</legend>
              <input
                {...field}
                className={styles.input}
                placeholder="Please specify"
                disabled={disabled}
              />
            </fieldset>
          )}
        />
      )}

      {errors?.[name] && <p className={styles.error}>{errors[name].message}</p>}
      {errors?.[`${name}Other`] && (
        <p className={styles.error}>{errors[`${name}Other`].message}</p>
      )}
    </div>
  );
};

export default MentorshipSelect;
